// Shared layout for the 4 SEO/GEO pillar pages
// (/cartographie-viticole · /geographie-du-vin · /appellations-viticoles ·
// /terroirs-viticoles).
//
// Renders from a structured `data/pillars.js` block (HTML strings) —
// no JSX in the source content — so the same block is trivially
// translatable via /api/pillar/{slug}?lang=xx (Mongo cache + LLM).
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Map, ShoppingBag, ExternalLink, AlertCircle, Loader2 } from "lucide-react";
import Layout, { Breadcrumb } from "@/components/Layout";
import { PILLARS } from "@/data/pillars";
import { useLang } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const LANG_TAG = { fr: "fr-FR", en: "en-US", es: "es-ES", pt: "pt-PT" };
const BC_HOME = { fr: "Accueil", en: "Home", es: "Inicio", pt: "Início" };
const RELATED_HEADING = { fr: "Poursuivez avec geoVINUM", en: "Continue with geoVINUM", es: "Continúa con geoVINUM", pt: "Continue com a geoVINUM" };
const RELATED_LABELS = {
  fr: { explorer: "Explorer", shop: "Cartes à offrir", academy: "Formations" },
  en: { explorer: "Explore", shop: "Wine maps", academy: "Courses" },
  es: { explorer: "Explorar", shop: "Mapas", academy: "Cursos" },
  pt: { explorer: "Explorar", shop: "Mapas", academy: "Cursos" },
};
const FAQ_HEADING = { fr: "Questions fréquentes", en: "Frequently asked questions", es: "Preguntas frecuentes", pt: "Perguntas frequentes" };
const TOC_HEADING = { fr: "Sommaire", en: "Contents", es: "Índice", pt: "Índice" };
const STALE_NOTICE = {
  en: "Translation is still running in the background — you may see the French version briefly.",
  es: "La traducción se está ejecutando en segundo plano — es posible que veas la versión francesa brevemente.",
  pt: "A tradução está a decorrer em segundo plano — poderá ver brevemente a versão francesa.",
};

const PillarPageLayout = ({ slug }) => {
  const lang = useLang() || "fr";
  const source = PILLARS[slug];
  const [remote, setRemote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (!source) return;
    if (lang === "fr") {
      setRemote(null);
      setStale(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { ok, data } = await apiFetch(`/api/pillar/${slug}?lang=${lang}`);
      if (cancelled) return;
      if (ok && data) {
        setRemote(data);
        setStale(!!data.stale);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug, lang, source]);

  // Merge translated fields on top of the FR source (which still
  // carries related links, breadcrumb, path, etc.).
  const view = useMemo(() => {
    if (!source) return null;
    if (!remote || lang === "fr") return source;
    return {
      ...source,
      h1: remote.h1 || source.h1,
      summary: remote.summary || source.summary,
      lead: remote.lead || source.lead,
      sections: (remote.sections || []).length ? remote.sections.map((s, i) => ({
        ...(source.sections[i] || {}),
        title: s.title || source.sections[i]?.title,
        body: s.body || source.sections[i]?.body,
      })) : source.sections,
      faqs: (remote.faqs || []).length ? remote.faqs : source.faqs,
    };
  }, [source, remote, lang]);

  const canonical = source ? `https://geovinum.io${source.path}${lang !== "fr" ? `?lang=${lang}` : ""}` : "";
  const toc = useMemo(
    () => (view?.sections || []).map((s) => ({ id: s.id || slugify(s.title), title: s.title })),
    [view]
  );

  useEffect(() => {
    if (!view) return;
    document.title = `${view.h1} · geoVINUM`;
    const desc = (view.summary || "").slice(0, 155);
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = desc;
    let c = document.querySelector('link[rel="canonical"]');
    if (!c) { c = document.createElement("link"); c.rel = "canonical"; document.head.appendChild(c); }
    c.href = canonical;
  }, [view, canonical]);

  if (!source) return null;

  const inLang = LANG_TAG[lang] || "fr-FR";
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: view.h1,
    description: view.summary,
    inLanguage: inLang,
    author: { "@type": "Organization", name: "geoVINUM", url: "https://geovinum.io" },
    publisher: { "@type": "Organization", name: "geoVINUM", logo: { "@type": "ImageObject", url: "https://geovinum.io/logo.png" } },
    datePublished: "2026-02-15",
    dateModified: "2026-02-15",
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: BC_HOME[lang] || "Accueil", item: "https://geovinum.io/" },
      { "@type": "ListItem", position: 2, name: lang === "fr" ? source.breadcrumbLabel : (view.h1 || source.breadcrumbLabel), item: canonical },
    ],
  };
  const faqLd = (view.faqs || []).length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: view.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null;

  return (
    <Layout testId={`pillar-${slug}`}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      {faqLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />}

      {/* Hero */}
      <header className="bg-[color:var(--gv-mist)]/40 border-b border-[color:var(--gv-line)]" data-testid="pillar-hero">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <Breadcrumb items={[
            { label: BC_HOME[lang] || "Accueil", to: "/" },
            // Breadcrumb label falls back to the translated H1 for non-FR
            // locales so the breadcrumb doesn't leak the raw FR string
            // (source.breadcrumbLabel is only defined in FR data).
            { label: lang === "fr" ? source.breadcrumbLabel : (view.h1 || source.breadcrumbLabel) },
          ]} />
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--gv-ink)] mt-4 mb-5 leading-[1.05]" data-testid="pillar-h1">
            {view.h1}
          </h1>
          <p className="text-lg text-[color:var(--gv-ink)]/80 max-w-3xl leading-relaxed" data-testid="pillar-summary">
            {view.summary}
          </p>
          {view.lead && (
            <div className="mt-6 text-[15px] text-[color:var(--gv-ink)]/75 max-w-3xl leading-relaxed">{view.lead}</div>
          )}
          {loading && (
            <div className="mt-4 inline-flex items-center gap-2 text-[13px] text-[color:var(--gv-ink)]/60" data-testid="pillar-loading">
              <Loader2 size={12} className="animate-spin" /> …
            </div>
          )}
          {stale && !loading && STALE_NOTICE[lang] && (
            <div className="mt-4 inline-flex items-start gap-2 text-[12.5px] text-[color:var(--gv-ink)]/70 border border-amber-300/60 bg-amber-50/50 rounded-[5px] px-3 py-2 max-w-2xl" data-testid="pillar-stale">
              <AlertCircle size={13} className="text-amber-500 shrink-0 mt-0.5" /> {STALE_NOTICE[lang]}
            </div>
          )}
        </div>
      </header>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-[240px,1fr] gap-10">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/50 mb-3">{TOC_HEADING[lang] || "Sommaire"}</div>
            <ul className="space-y-2 text-[13.5px]" data-testid="pillar-toc">
              {toc.map((t) => (
                <li key={t.id}>
                  <a href={`#${t.id}`} className="text-[color:var(--gv-ink)]/75 hover:text-[color:var(--gv-pink)] block leading-snug">
                    {t.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <article className="prose prose-lg max-w-none" data-testid="pillar-article">
          {(view.sections || []).map((s) => {
            const id = s.id || slugify(s.title);
            return (
              <section key={id} id={id} className="mb-12 scroll-mt-24" data-testid={`pillar-section-${id}`}>
                <h2 className="font-display text-2xl md:text-3xl text-[color:var(--gv-ink)] mb-4">{s.title}</h2>
                <div
                  className="text-[15px] text-[color:var(--gv-ink)]/85 leading-relaxed space-y-4 pillar-body"
                  dangerouslySetInnerHTML={{ __html: s.body || "" }}
                />
              </section>
            );
          })}

          {(view.faqs || []).length > 0 && (
            <section id="faq" className="mb-12 scroll-mt-24" data-testid="pillar-faq">
              <h2 className="font-display text-2xl md:text-3xl text-[color:var(--gv-ink)] mb-6">{FAQ_HEADING[lang] || "Questions fréquentes"}</h2>
              <div className="space-y-6">
                {view.faqs.map((f, i) => (
                  <div key={i} className="border-l-2 border-[color:var(--gv-pink)] pl-4">
                    <div className="font-medium text-[color:var(--gv-ink)] mb-2">{f.q}</div>
                    <div className="text-[14.5px] text-[color:var(--gv-ink)]/75 leading-relaxed">{f.a}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {(source.related?.explorer?.length || source.related?.shop?.length || source.related?.academy?.length) ? (
            <section className="mb-8 border-t border-[color:var(--gv-line)] pt-8" data-testid="pillar-related">
              <h2 className="font-display text-2xl text-[color:var(--gv-ink)] mb-6">{RELATED_HEADING[lang] || "Poursuivez avec geoVINUM"}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {source.related.explorer?.length ? (
                  <RelatedCard Icon={Map} title={RELATED_LABELS[lang].explorer} links={source.related.explorer} testid="related-explorer" />
                ) : null}
                {source.related.shop?.length ? (
                  <RelatedCard Icon={ShoppingBag} title={RELATED_LABELS[lang].shop} links={source.related.shop} testid="related-shop" />
                ) : null}
                {source.related.academy?.length ? (
                  <RelatedCard Icon={BookOpen} title={RELATED_LABELS[lang].academy} links={source.related.academy} testid="related-academy" />
                ) : null}
              </div>
            </section>
          ) : null}
        </article>
      </div>
    </Layout>
  );
};

const RelatedCard = ({ Icon, title, links, testid }) => (
  <div className="border border-[color:var(--gv-line)] rounded-[6px] p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} className="text-[color:var(--gv-pink)]" />
      <div className="text-[12px] uppercase tracking-widest text-[color:var(--gv-ink)]/55">{title}</div>
    </div>
    <ul className="space-y-2 text-[14px]">
      {links.map((l, i) => (
        <li key={i}>
          {l.external ? (
            <a href={l.href || l.to} target="_blank" rel="noreferrer" className="text-[color:var(--gv-ink)] hover:text-[color:var(--gv-pink)] inline-flex items-center gap-1">
              {l.label} <ExternalLink size={11} className="opacity-50" />
            </a>
          ) : (
            <Link to={l.to} className="text-[color:var(--gv-ink)] hover:text-[color:var(--gv-pink)] inline-flex items-center gap-1">
              {l.label} <ArrowRight size={11} className="opacity-50" />
            </Link>
          )}
        </li>
      ))}
    </ul>
  </div>
);

export default PillarPageLayout;
